import { getBezierEdgeCenter } from '@xyflow/react';
import {
  extractWorkflowExpressionReferences,
  pruneZipTies,
  readManualExpressionOverrides,
  uniqueWires,
  zipTieWireKey,
  type ZipTie,
  type ZipTieWire,
} from '@erp/workflows';

/**
 * Geometry for the workflow canvas zip ties. The gestures match the wire
 * bundling people have been passing around for node editors: hold Z and drag
 * across edges to strap them into a ribbon, drag the strap to move it,
 * Shift-drag to rotate it, and drag across it again (or double-click) to cut.
 *
 * Routing follows the same idea as that prototype. A tie has a travel direction
 * (`angle`) and a strand order (`wires`). Each edge is drawn as cubic
 * segments that pass through its strand point, with the handles at a tie aimed
 * along the tie so the ribbon runs straight between straps. Untied edges use
 * React Flow's own bezier handles, so a wire that isn't in a tie looks unchanged.
 */

const STRAND_SPACING = 8;
export const BAR_HALF_WIDTH = 3;
export const BAR_PAD = 4;
export const CONTROL_FACTOR = 0.25;
const CURVATURE = 0.25;
export const SAMPLES = 24;
export const HIT_PAD = 8;
export const MIN_STROKE = 4;
export const ANGLE_SNAP = Math.PI / 12;
export const DOUBLE_CLICK_MS = 350;
export const ZIP_TIE_GREY = '#64748b';

export const ZIP_TIE_PALETTE = [
  { name: 'Red', color: '#e5484d' },
  { name: 'Orange', color: '#f76b15' },
  { name: 'Yellow', color: '#ffc53d' },
  { name: 'Green', color: '#46a758' },
  { name: 'Teal', color: '#12a594' },
  { name: 'Blue', color: '#3e63dd' },
  { name: 'Purple', color: '#8e4ec6' },
  { name: 'Pink', color: '#d6409f' },
  { name: 'White', color: '#ededed' },
  { name: 'Black', color: '#232326' },
] as const;

const RANDOM_SKIP = new Set(['#ededed', '#232326']);

export type Point = { x: number; y: number };

export type FlowHandlePosition = 'left' | 'right' | 'top' | 'bottom';

export type EdgeGeometry = {
  id: string;
  /** The sockets this stroke belongs to. Absent for a wire a tie cannot hold. */
  wire?: ZipTieWire;
  source: Point;
  target: Point;
  sourcePosition: FlowHandlePosition;
  targetPosition: FlowHandlePosition;
};

type RoutedSegment = {
  source: Point;
  target: Point;
  sourceControl: Point;
  targetControl: Point;
};

type StrokeHit = {
  wire: ZipTieWire;
  t: number;
  tangent: Point;
  point: Point;
};

function calculateControlOffset(distance: number, curvature: number): number {
  if (distance >= 0) return 0.5 * distance;
  return curvature * 25 * Math.sqrt(-distance);
}

function controlFor(
  position: FlowHandlePosition,
  from: Point,
  to: Point,
): Point {
  switch (position) {
    case 'left':
      return {
        x: from.x - calculateControlOffset(from.x - to.x, CURVATURE),
        y: from.y,
      };
    case 'right':
      return {
        x: from.x + calculateControlOffset(to.x - from.x, CURVATURE),
        y: from.y,
      };
    case 'top':
      return {
        x: from.x,
        y: from.y - calculateControlOffset(from.y - to.y, CURVATURE),
      };
    case 'bottom':
      return {
        x: from.x,
        y: from.y + calculateControlOffset(to.y - from.y, CURVATURE),
      };
  }
}

function tieAxes(angle: number): { tangent: Point; normal: Point } {
  const tangent = { x: Math.cos(angle), y: Math.sin(angle) };
  return { tangent, normal: { x: -tangent.y, y: tangent.x } };
}

export function tieHalfLength(tie: ZipTie): number {
  return ((tie.wires.length - 1) * STRAND_SPACING) / 2 + BAR_PAD;
}

export function strandPoint(tie: ZipTie, wireKey: string): Point {
  const index = tie.wires.findIndex((wire) => zipTieWireKey(wire) === wireKey);
  const offset = (index - (tie.wires.length - 1) / 2) * STRAND_SPACING;
  const { normal } = tieAxes(tie.angle);
  return {
    x: tie.position.x + normal.x * offset,
    y: tie.position.y + normal.y * offset,
  };
}

function projection(point: Point, origin: Point, toward: Point): number {
  return (
    (point.x - origin.x) * (toward.x - origin.x) +
    (point.y - origin.y) * (toward.y - origin.y)
  );
}

/** Ties this edge passes through, from its source toward its target. */
function orderedTies(
  ties: readonly ZipTie[],
  wireKey: string,
  source: Point,
  target: Point,
): ZipTie[] {
  return ties
    .filter((tie) => tie.wires.some((wire) => zipTieWireKey(wire) === wireKey))
    .sort(
      (left, right) =>
        projection(left.position, source, target) -
        projection(right.position, source, target),
    );
}

function directedControl(at: Point, direction: Point, distance: number): Point {
  return {
    x: at.x + direction.x * distance,
    y: at.y + direction.y * distance,
  };
}

/** Sub-segments one edge is drawn as. Outer ends keep React Flow's handles. */
export function routeEdge(
  edge: EdgeGeometry,
  ties: readonly ZipTie[],
): RoutedSegment[] {
  const wireKey = edge.wire ? zipTieWireKey(edge.wire) : '';
  const through = wireKey
    ? orderedTies(ties, wireKey, edge.source, edge.target)
    : [];
  if (through.length === 0) {
    return [
      {
        source: edge.source,
        target: edge.target,
        sourceControl: controlFor(
          edge.sourcePosition,
          edge.source,
          edge.target,
        ),
        targetControl: controlFor(
          edge.targetPosition,
          edge.target,
          edge.source,
        ),
      },
    ];
  }

  const stops = [
    { at: edge.source, tangent: null as Point | null },
    ...through.map((tie) => ({
      at: strandPoint(tie, wireKey),
      tangent: tieAxes(tie.angle).tangent,
    })),
    { at: edge.target, tangent: null },
  ];
  const segments: RoutedSegment[] = [];
  for (let index = 0; index < stops.length - 1; index++) {
    const prev = stops[index];
    const next = stops[index + 1];
    if (!prev || !next) continue;
    const distance =
      Math.hypot(next.at.x - prev.at.x, next.at.y - prev.at.y) * CONTROL_FACTOR;
    segments.push({
      source: prev.at,
      target: next.at,
      sourceControl: prev.tangent
        ? directedControl(prev.at, prev.tangent, distance)
        : controlFor(edge.sourcePosition, prev.at, next.at),
      targetControl: next.tangent
        ? directedControl(next.at, next.tangent, -distance)
        : controlFor(edge.targetPosition, next.at, prev.at),
    });
  }
  return segments;
}

export function segmentsToPath(segments: readonly RoutedSegment[]): string {
  const [first] = segments;
  if (!first) return '';
  const commands = segments.map(
    (segment) =>
      `C${segment.sourceControl.x},${segment.sourceControl.y} ${segment.targetControl.x},${segment.targetControl.y} ${segment.target.x},${segment.target.y}`,
  );
  return `M${first.source.x},${first.source.y} ${commands.join(' ')}`;
}

/** Centre of a cubic at t = 0.5, where the edge's delete button sits. */
export function segmentLabel(segment: RoutedSegment): Point {
  const [x, y] = getBezierEdgeCenter({
    sourceX: segment.source.x,
    sourceY: segment.source.y,
    targetX: segment.target.x,
    targetY: segment.target.y,
    sourceControlX: segment.sourceControl.x,
    sourceControlY: segment.sourceControl.y,
    targetControlX: segment.targetControl.x,
    targetControlY: segment.targetControl.y,
  });
  return { x, y };
}

function segmentPolyline(segment: RoutedSegment, samples = SAMPLES): Point[] {
  const points: Point[] = [];
  for (let index = 0; index <= samples; index++) {
    const t = index / samples;
    const u = 1 - t;
    const w0 = u * u * u;
    const w1 = 3 * u * u * t;
    const w2 = 3 * u * t * t;
    const w3 = t * t * t;
    points.push({
      x:
        w0 * segment.source.x +
        w1 * segment.sourceControl.x +
        w2 * segment.targetControl.x +
        w3 * segment.target.x,
      y:
        w0 * segment.source.y +
        w1 * segment.sourceControl.y +
        w2 * segment.targetControl.y +
        w3 * segment.target.y,
    });
  }
  return points;
}

/** Parameter along p→q where it crosses r→s, or null when they miss. */
function intersectSegments(
  p: Point,
  q: Point,
  r: Point,
  s: Point,
): number | null {
  const d1x = q.x - p.x;
  const d1y = q.y - p.y;
  const d2x = s.x - r.x;
  const d2y = s.y - r.y;
  const denominator = d1x * d2y - d1y * d2x;
  if (Math.abs(denominator) < 1e-9) return null;
  const ex = r.x - p.x;
  const ey = r.y - p.y;
  const t = (ex * d2y - ey * d2x) / denominator;
  const u = (ex * d1y - ey * d1x) / denominator;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? t : null;
}

function tieBar(tie: ZipTie): [Point, Point] {
  const half = tieHalfLength(tie);
  const { normal } = tieAxes(tie.angle);
  return [
    {
      x: tie.position.x - normal.x * half,
      y: tie.position.y - normal.y * half,
    },
    {
      x: tie.position.x + normal.x * half,
      y: tie.position.y + normal.y * half,
    },
  ];
}

export function tieAtPoint(
  ties: readonly ZipTie[],
  point: Point,
  zoom: number,
): ZipTie | null {
  const pad = HIT_PAD / (zoom || 1);
  for (let index = ties.length - 1; index >= 0; index--) {
    const tie = ties[index];
    if (!tie) continue;
    const { tangent, normal } = tieAxes(tie.angle);
    const dx = point.x - tie.position.x;
    const dy = point.y - tie.position.y;
    const along = dx * tangent.x + dy * tangent.y;
    const across = dx * normal.x + dy * normal.y;
    if (
      Math.abs(along) <= BAR_HALF_WIDTH + pad &&
      Math.abs(across) <= tieHalfLength(tie) + pad
    ) {
      return tie;
    }
  }
  return null;
}

function edgePolyline(edge: EdgeGeometry, ties: readonly ZipTie[]): Point[] {
  const points: Point[] = [];
  for (const segment of routeEdge(edge, ties)) {
    const sampled = segmentPolyline(segment);
    points.push(...(points.length > 0 ? sampled.slice(1) : sampled));
  }
  return points;
}

/**
 * What a stroke from `start` to `end` would do. A stroke that crosses a tie
 * cuts those ties; otherwise it ties the wires it crosses.
 */
function segmentHit(
  wire: ZipTieWire,
  start: Point,
  end: Point,
  prev: Point | undefined,
  next: Point | undefined,
): StrokeHit | null {
  if (!prev || !next) return null;
  const t = intersectSegments(start, end, prev, next);
  if (t === null) return null;
  const dx = next.x - prev.x;
  const dy = next.y - prev.y;
  const length = Math.max(Math.hypot(dx, dy), 1e-9);
  return {
    wire,
    t,
    tangent: { x: dx / length, y: dy / length },
    point: {
      x: start.x + (end.x - start.x) * t,
      y: start.y + (end.y - start.y) * t,
    },
  };
}

/** The earliest place `start → end` crosses a sampled wire, if it crosses one. */
export function bestStrokeHit(
  wire: ZipTieWire,
  poly: readonly Point[],
  start: Point,
  end: Point,
): StrokeHit | null {
  let best: StrokeHit | null = null;
  for (let index = 1; index < poly.length; index++) {
    const hit = segmentHit(wire, start, end, poly[index - 1], poly[index]);
    if (hit && (best === null || hit.t < best.t)) best = hit;
  }
  return best;
}

/** What a stroke can hit, sampled once per stroke rather than per pointermove. */
export type StrokeTargets = {
  bars: { id: string; bar: [Point, Point] }[];
  wires: { wire: ZipTieWire; poly: Point[] }[];
};

export function strokeTargets(
  edges: readonly EdgeGeometry[],
  ties: readonly ZipTie[],
): StrokeTargets {
  const wires: StrokeTargets['wires'] = [];
  for (const edge of edges) {
    if (edge.wire)
      wires.push({ wire: edge.wire, poly: edgePolyline(edge, ties) });
  }
  return {
    bars: ties.map((tie) => ({ id: tie.id, bar: tieBar(tie) })),
    wires,
  };
}

export function classifyStroke(
  targets: StrokeTargets,
  start: Point,
  end: Point,
): { cutTieIds: string[]; hits: StrokeHit[] } {
  const cutTieIds = targets.bars
    .filter(({ bar }) => intersectSegments(start, end, bar[0], bar[1]) !== null)
    .map(({ id }) => id);
  if (cutTieIds.length > 0) return { cutTieIds, hits: [] };

  const hits: StrokeHit[] = [];
  for (const { wire, poly } of targets.wires) {
    const best = bestStrokeHit(wire, poly, start, end);
    if (best) hits.push(best);
  }
  return { cutTieIds, hits };
}

function pickZipTieColor(
  ties: readonly ZipTie[],
  random: () => number = Math.random,
): string {
  const last = ties.at(-1)?.color;
  const pool = ZIP_TIE_PALETTE.filter(
    (swatch) => !RANDOM_SKIP.has(swatch.color) && swatch.color !== last,
  );
  const choice = pool[Math.floor(random() * pool.length)] ?? ZIP_TIE_PALETTE[0];
  return choice.color;
}

/**
 * The tie a completed stroke creates, or null when it didn't cross a wire.
 * When some existing tie already holds every crossed wire, the new tie copies
 * that strand order so the ribbon doesn't cross itself between straps.
 */
export function tieFromStroke(
  hits: readonly StrokeHit[],
  ties: readonly ZipTie[],
  newId: () => string,
  random: () => number = Math.random,
): ZipTie | null {
  if (hits.length === 0) return null;
  let tx = 0;
  let ty = 0;
  let cx = 0;
  let cy = 0;
  for (const hit of hits) {
    tx += hit.tangent.x;
    ty += hit.tangent.y;
    cx += hit.point.x;
    cy += hit.point.y;
  }
  const angle = Math.hypot(tx, ty) > 1e-6 ? Math.atan2(ty, tx) : 0;
  const { normal } = tieAxes(angle);
  const ordered = [...hits].sort(
    (left, right) =>
      (left.point.x - right.point.x) * normal.x +
      (left.point.y - right.point.y) * normal.y,
  );
  const wires = uniqueWires(ordered.map((hit) => hit.wire));
  const keys = wires.map(zipTieWireKey);
  const twin = ties.find((tie) => {
    const held = new Set(tie.wires.map(zipTieWireKey));
    return keys.every((key) => held.has(key));
  });
  if (twin) {
    const rank = new Map(
      twin.wires.map((wire, index) => [zipTieWireKey(wire), index]),
    );
    wires.sort(
      (left, right) =>
        (rank.get(zipTieWireKey(left)) ?? 0) -
        (rank.get(zipTieWireKey(right)) ?? 0),
    );
  }
  return {
    id: newId(),
    position: { x: cx / hits.length, y: cy / hits.length },
    angle,
    wires,
    color: pickZipTieColor(ties, random),
  };
}

export function snapAngle(angle: number, snap: boolean): number {
  if (!snap) return angle;
  const turns = Math.round(angle / ANGLE_SNAP);
  return turns * ANGLE_SNAP;
}

const WIRE_FIELDS = [
  'sourceStepId',
  'sourceSocketId',
  'targetStepId',
  'targetSocketId',
] as const;

/** Plain shape check, not a zod parse: this runs for every edge on every render. */
function isZipTieWire(value: unknown): value is ZipTieWire {
  if (!value || typeof value !== 'object') return false;
  const fields: Partial<Record<keyof ZipTieWire, unknown>> = value;
  return WIRE_FIELDS.every((field) => {
    const part = fields[field];
    return typeof part === 'string' && part !== '';
  });
}

/** The sockets a rendered edge stands for, or null when it is not a strand. */
export function zipTieWireFromEdge(edge: {
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  data?: unknown;
}): ZipTieWire | null {
  const data =
    edge.data && typeof edge.data === 'object'
      ? (edge.data as { zipTieWire?: unknown }).zipTieWire
      : undefined;
  // An edge that names its wire (or names null, like an expression badge's
  // stub) is taken at its word rather than read from its handles.
  if (data !== undefined) return isZipTieWire(data) ? data : null;
  const sourceSocketId = edge.sourceHandle ?? '';
  const targetSocketId = edge.targetHandle ?? '';
  if (!edge.source || !edge.target || !sourceSocketId || !targetSocketId) {
    return null;
  }
  return {
    sourceStepId: edge.source,
    sourceSocketId,
    targetStepId: edge.target,
    targetSocketId,
  };
}

function addExpressionWires(
  node: { id: string; data?: unknown },
  keys: Set<string>,
): void {
  const config = (node.data as { config?: Record<string, unknown> } | undefined)
    ?.config;
  const overrides = readManualExpressionOverrides(config);
  for (const [socketId, expression] of Object.entries(overrides)) {
    if (typeof expression !== 'string') continue;
    const refs = expressionRefs(expression);
    for (const ref of refs) {
      keys.add(
        zipTieWireKey({
          sourceStepId: ref.stepId,
          sourceSocketId: ref.socketId,
          targetStepId: node.id,
          targetSocketId: socketId,
        }),
      );
    }
  }
}

function expressionRefs(
  expression: string,
): { stepId: string; socketId: string }[] {
  try {
    return extractWorkflowExpressionReferences(expression);
  } catch {
    return [];
  }
}

/**
 * Socket pairs currently on the canvas: stored edges, plus wires a manual
 * socket expression draws that have no edge row.
 */
export function canvasZipTieWireKeys(
  nodes: readonly { id: string; data?: unknown }[],
  edges: readonly {
    source: string;
    target: string;
    sourceHandle?: string | null;
    targetHandle?: string | null;
  }[],
): Set<string> {
  const keys = new Set<string>();
  for (const edge of edges) {
    const wire = zipTieWireFromEdge(edge);
    if (wire) keys.add(zipTieWireKey(wire));
  }
  for (const node of nodes) addExpressionWires(node, keys);
  return keys;
}

/**
 * Keep the strands whose sockets are still on the canvas. Membership is the
 * socket pair, so a reconnected edge with a new id stays in the tie.
 */
export function projectZipTies(
  ties: readonly ZipTie[],
  validWireKeys: Iterable<string>,
): ZipTie[] {
  const next = pruneZipTies(ties, validWireKeys);
  const unchanged =
    next.length === ties.length &&
    next.every((tie, index) => tie === ties[index]);
  return unchanged ? (ties as ZipTie[]) : next;
}

/**
 * Move a tie by the same amount as its wires when every node those wires touch
 * moved together (a group drag, or dragging the whole selection). A tie stays
 * put when only one end moves, so the ribbon stretches with that node.
 */
export function shiftTiesFollowingAnchors(
  ties: readonly ZipTie[],
  before: ReadonlyMap<string, Point>,
  after: ReadonlyMap<string, Point>,
): ZipTie[] {
  let changed = false;
  const next: ZipTie[] = [];
  for (const tie of ties) {
    const delta = sharedAnchorDelta(anchorIds(tie), before, after);
    if (!delta) {
      next.push(tie);
      continue;
    }
    changed = true;
    next.push({
      ...tie,
      position: { x: tie.position.x + delta.x, y: tie.position.y + delta.y },
    });
  }
  return changed ? next : (ties as ZipTie[]);
}

function anchorIds(tie: ZipTie): string[] {
  const nodeIds: string[] = [];
  for (const wire of tie.wires) {
    nodeIds.push(wire.sourceStepId, wire.targetStepId);
  }
  return nodeIds;
}

/** The move shared by every anchor, or null when they disagree or nobody moved. */
export function sharedAnchorDelta(
  nodeIds: readonly string[],
  before: ReadonlyMap<string, Point>,
  after: ReadonlyMap<string, Point>,
): Point | null {
  let delta: Point | null = null;
  for (const nodeId of nodeIds) {
    const from = before.get(nodeId);
    const to = after.get(nodeId);
    if (!from || !to) return null;
    const step = { x: to.x - from.x, y: to.y - from.y };
    if (
      delta &&
      (Math.abs(step.x - delta.x) > 0.01 || Math.abs(step.y - delta.y) > 0.01)
    ) {
      return null;
    }
    delta = step;
  }
  if (!delta || (delta.x === 0 && delta.y === 0)) return null;
  return delta;
}
